import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StyledButton } from "@insite/mobius/Clickable/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React from "react";
import { css } from "styled-components";

const enum fields {
    sections = "sections",
    title = "title",
}

interface InventoryRes {
    fields: {
        imageTitle: string;
        image: string;
        imageLink: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sections]: InventoryRes[];
        [fields.title]: string;
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    Title?: InjectableCss;
    infoCardContainer?: GridContainerProps;
    infoCardImage?: LazyImageProps;
    infoCardDiv?: GridItemProps;
    imageTitle?: InjectableCss;
    main?: InjectableCss;
}

export const infoCardStyles: InfoCardStyles = {
    infoCardContainer: {
        gap: 40,
        css: css`
            margin: 0 auto;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            width: 99%;
        `,
    },
    infoCardDiv: {
        width: [12, 12, 12, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            position: relative;
            width: 50%;

            ${TypographyStyle} {
                border: 1px solid #efefef;
                background-color: #efefef;
                margin-bottom: 0;
            }

            &:hover {
                ${TypographyStyle} {
                    text-decoration: underline;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 9px 20px;
                    `,
                    css`
                        padding: 9px 20px;
                    `,
                    css`
                        padding: 9px 0px;

                        flex-basis: 620px;
                        margin: 0 auto !important;
                        max-width: max-content;
                    `,
                    null,
                    null,
                ])};
        `,
    },

    infoCardImage: {
        css: css`
            width: 100%;
            display: flex;
            img {
                opacity: 1;
                //object-fit: contain;
                display: block;
                width: 100%;
                height: 187px;
                border: 1px solid #efefef;
                background-color: #efefef;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 10px;
                        `,
                        css`
                            padding: 10px;
                        `,
                        css`
                            padding: 10px;

                            height: auto;
                        `,
                        css``,
                        css``,
                    ])}
            }
        `,
    },
    imageTitle: {
        css: css`
            font-size: 14.4px;
            text-align: initial;
            padding: 10px;
            font-family: "Roboto-medium";
            color: ${getColor("primary")};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 8px;
                        font-size: 17px;
                        padding: 0px 20px;
                    `,
                    css`
                        margin-top: 8px;
                        font-size: 17px;
                        padding: 0px 20px;
                    `,
                    css`
                        margin-top: 8px;
                        font-size: 17px;
                        padding: 0px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    Title: {
        css: css`
            background-color: ${getColor("common.background")};
            font-size: 28px;
            font-family: "Roboto-bold";
            text-align: center;
            margin-top: 0;
            text-transform: capitalize;
            font-weight: 400;
            color: ${getColor("primary")};
            padding-bottom: 15px;
            margin-bottom: 24px;
            margin-top: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 14px;

                        padding-bottom: 14px;
                    `,
                    css`
                        margin-bottom: 14px;

                        padding-bottom: 14px;
                    `,
                    css`
                        margin-bottom: 14px;

                        padding-bottom: 14px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    main: {
        css: css`
            margin-top: 33px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = infoCardStyles;

const BlogComp: React.FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper>
            <StyledWrapper {...styles.Title}>{fields.title}</StyledWrapper>
            <StyledWrapper {...styles.main}>
                <GridContainer {...styles.infoCardContainer}>
                    {fields.sections.map((section, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <GridItem key={index} {...styles.infoCardDiv}>
                            <Link href={section.fields.imageLink} target="_blank">
                                <StyledWrapper {...styles.imageTitle}>{section.fields.imageTitle}</StyledWrapper>
                                <LazyImage src={section.fields.image} {...styles.infoCardImage}></LazyImage>
                            </Link>
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BlogComp,
    definition: {
        group: "Basic",
        displayName: "SE Blog Widget",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Tittle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.sections,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields) => {
                    return item.fields.imageTitle;
                },
                fieldDefinitions: [
                    {
                        name: "imageTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageLink",
                        editorTemplate: "TextField",
                        displayName: "Url",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
